import React, { useState, useEffect, useRef, useCallback } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import {
  Box,
  Typography,
  Button,
  List,
  ListItemButton,
  Checkbox,
  ListItemText,
  Skeleton,
  Divider,
  CircularProgress,
} from "@mui/material"
import { formatColumnOption } from "./tableUtils"

interface DynamicColumnFilterProps {
  columnName: string
  label: string
  fetchValuesApi: (offset?: number) => Promise<{items: string[], total_count: number, has_more: boolean, next_offset: number, has_empty_values?: boolean}>
  selectedValues: string[]
  onChange: (values: string[]) => void
  cacheKey?: string
  colDef?: any
  formatOptionLabel?: (opt: string) => string
}

let cachedInitialSelections: { key: string; selections: Set<string>; timeout: any } | null = null


export const DynamicColumnFilter: React.FC<DynamicColumnFilterProps> = ({
  columnName,
  label,
  fetchValuesApi,
  selectedValues,
  onChange,
  cacheKey,
  colDef,
  formatOptionLabel,
}) => {

  const [initialSelectedValues] = useState(() => {
    const key = cacheKey || columnName
    if (cachedInitialSelections && cachedInitialSelections.key === key) {
      if (cachedInitialSelections.timeout) {
        clearTimeout(cachedInitialSelections.timeout)
        cachedInitialSelections.timeout = null
      }
      return cachedInitialSelections.selections
    }
    if (cachedInitialSelections?.timeout) {
      clearTimeout(cachedInitialSelections.timeout)
    }
    const selections = new Set(selectedValues)
    cachedInitialSelections = { key, selections, timeout: null }
    return selections
  })

  useEffect(() => {
    return () => {
      const key = cacheKey || columnName
      if (cachedInitialSelections && cachedInitialSelections.key === key) {
        cachedInitialSelections.timeout = setTimeout(() => {
          if (cachedInitialSelections && cachedInitialSelections.key === key) {
            cachedInitialSelections = null
          }
        }, 500)
      }
    }
  }, [cacheKey, columnName])

  const queryKey = React.useMemo(() => ['dynamicColumn', cacheKey || columnName], [cacheKey, columnName])

  const {
    data,
    isLoading,
    isFetchingNextPage: isLoadingMore,
    hasNextPage: hasMore,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return await fetchValuesApi(pageParam as number)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.has_more ? lastPage.next_offset : undefined,
    staleTime: 15 * 1000,
  })

  const options = React.useMemo(() => {
    if (!data) return []
    const allItems = data.pages.flatMap(page => page.items || [])
    const uniqueItems = Array.from(new Set(allItems))
    if (data.pages[0]?.has_empty_values) {
      uniqueItems.unshift('__empty__')
    }
    return uniqueItems
  }, [data])

  const totalCount = (data?.pages[0]?.total_count || 0) + (data?.pages[0]?.has_empty_values ? 1 : 0)

  const observer = useRef<IntersectionObserver | null>(null)

  const lastElementRef = useCallback((node: HTMLElement | null) => {
    if (isLoading || isLoadingMore) return
    if (observer.current) observer.current.disconnect()
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchNextPage()
      }
    }, {
      rootMargin: '100px'
    })
    
    if (node) observer.current.observe(node)
  }, [isLoading, isLoadingMore, hasMore, fetchNextPage])

  const handleToggle = (value: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    let next: string[]
    if (selectedValues.includes(value)) {
      next = selectedValues.filter(v => v !== value)
    } else {
      next = [...selectedValues, value]
    }
    onChange(next)
  }

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
  }

  const sortedOptions = React.useMemo(() => {
    return [...options].sort((a, b) => {
      if (a === '__empty__') return -1;
      if (b === '__empty__') return 1;
      const aSelected = initialSelectedValues.has(a) ? 1 : 0
      const bSelected = initialSelectedValues.has(b) ? 1 : 0
      return bSelected - aSelected
    })
  }, [options, initialSelectedValues])

  return (
    <Box
      sx={{
        minWidth: 220,
        display: "flex",
        flexDirection: "column",
        maxHeight: 280,
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" fontWeight={500}>
          Filter by {label}
        </Typography>
        {selectedValues.length > 0 && (
          <Button
            size="small"
            onClick={handleClear}
            sx={{ fontSize: "0.75rem", p: 0, minWidth: "auto", textTransform: "none" }}
          >
            Reset
          </Button>
        )}
      </Box>
      <Divider />
      <Box sx={{ overflowY: "auto", pt: 1, pb: 0 }}>
        <List dense disablePadding>
          {isLoading && options.length === 0 ? (
            Array.from({ length: 5 }).map((_, i) => (
              <ListItemButton key={`initial-skeleton-${i}`} dense sx={{ py: 0.5, px: 2 }}>
                <Box sx={{ p: 0.5, mr: 1, display: 'flex' }}>
                  <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} />
                </Box>
                <Skeleton variant="text" width={`${50 + (i % 3) * 15}%`} height={20} />
              </ListItemButton>
            ))
          ) : options.length === 0 && !isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: "center" }}>
              No options available
            </Typography>
          ) : (
            sortedOptions.map((opt) => (
              <ListItemButton
                key={opt}
                dense
                onClick={(e) => handleToggle(opt, e)}
                sx={{ py: 0.5, px: 2 }}
              >
                <Checkbox
                  size="small"
                  checked={selectedValues.includes(opt)}
                  edge="start"
                  disableRipple
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText
                  primary={
                    opt === '__empty__'
                      ? `No ${label}`
                      : formatColumnOption(colDef, opt, formatOptionLabel)
                  }
                  primaryTypographyProps={{ 
                    variant: "body1",
                    fontSize: "0.925rem",
                    fontWeight: opt === '__empty__' ? 500 : 400,
                    color: opt === '__empty__' ? 'text.secondary' : 'text.primary',
                  }}
                />
              </ListItemButton>
            ))
          )}
          
          {/* Sentinel element for IntersectionObserver */}
          {hasMore && !isLoading && (
            <li ref={lastElementRef} style={{ height: 1 }} />
          )}

          {isLoadingMore && (
            <Box component="li" sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} thickness={4} />
            </Box>
          )}
        </List>
      </Box>
      {!isLoading && totalCount > 20 && (
        <Box sx={{ px: 2, pt: 0.6 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
            Showing {options.length} of {totalCount} options
          </Typography>
        </Box>
      )}
    </Box>
  )
}
